import React, { FC, useCallback, useEffect, useState } from 'react';
import cx from 'classnames';
import debounce from 'lodash/debounce';

import { Search } from 'components/icons';
import { Override } from 'types/common';
import { SelectAjaxProps, SelectProps as BaseSelectProps } from 'types/components';

import Spin from '../Spin';

import styles from './styles.module.css';

import Select from '.';

type SelectOptions = NonNullable<BaseSelectProps['options']>;

type SelectProps = Override<SelectAjaxProps, {
  fetchDefaultOptions: (value: SelectAjaxProps['value']) => Promise<SelectOptions>;
  fetchOptions: (query: string) => Promise<SelectOptions>;
}>;

const AjaxSelect: FC<SelectProps> = ({
  className,
  popupClassName,
  value,
  prefix = <Search />,
  fetchDefaultOptions,
  fetchOptions,
  ...props
}) => {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<SelectOptions>();

  const loadOptions = useCallback(async (callback: () => Promise<SelectOptions>) => {
    if (loading) {
      return;
    }

    setOptions([]);
    setLoading(true);

    try {
      const options = await callback();

      setOptions(options);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const handleSearch = (query: string) => loadOptions(() => fetchOptions(query));

  useEffect(() => {
    if (value && !options) {
      loadOptions(() => fetchDefaultOptions(value));
    }
  }, [value, options, fetchDefaultOptions, loadOptions]);

  return (
    <Select
      {...props}
      className={cx(styles.ajax, className)}
      popupClassName={cx(styles.ajaxPopup, popupClassName)}
      options={options}
      value={value}
      notFoundContent={loading ? <Spin size="small" /> : null}
      prefix={prefix}
      filterOption={false}
      showSearch
      loading={loading}
      onSearch={debounce(handleSearch, 500)}
    />
  );
};

export default AjaxSelect;
